import classNames from "classnames";
import { useMemo } from "react";
import ReactTooltip from "react-tooltip";

import {
  CaseSpecimenAdmin,
  Lab,
  SpecimenAdmin,
  SpecimenProtocolOverride,
} from "pwapi";

import { CaseAdminDetail } from "shared/reducers/admin/adminCaseReducer";
import {
  getProtocolStatuses,
  protocolLabels,
} from "shared/utils/specimenUtils";

import { useAppSelector } from "store/hooks";

const SpecimenFormProtocols = ({
  caseType,
  specimen,
  setFieldValue,
  accessioningLab,
}: {
  caseType: CaseAdminDetail["type"];
  specimen: SpecimenAdmin | CaseSpecimenAdmin;
  setFieldValue: Function;
  accessioningLab?: Lab;
}) => {
  const protocolOverrides =
    (specimen as SpecimenAdmin).protocol_overrides || [];
  const protocolStatuses = getProtocolStatuses(
    specimen,
    true,
    accessioningLab?.settings,
    caseType
  );

  const setProtocolOverride = (
    p: SpecimenProtocolOverride.protocol,
    v: string
  ) => {
    const newProtocolOverrides = protocolOverrides.filter(
      (po) => po.protocol !== p
    );

    if (v !== "null") {
      newProtocolOverrides.push({ protocol: p, active: v === "true" });
    }

    setFieldValue("protocol_overrides", newProtocolOverrides);
  };

  const protocolsStr = Object.entries(
    getProtocolStatuses(specimen, undefined, accessioningLab?.settings)
  )
    .filter(([_, status]) => status.active)
    .map(([protocol, _]) => protocol)
    .join(",");
  const initialProtocolsStr = useMemo(() => protocolsStr, []);

  const protocolsToShow = ["alopecia", "nail" /* "whole_specimen" */];

  const difEnabled = useAppSelector(
    (state) => !!state.sys.features.switches?.difProtocol
  );
  if (difEnabled) protocolsToShow.push("dif");

  const pashReflexEnabled = accessioningLab?.settings?.pash_reflex?.active;
  const pasfReflexEnabled = accessioningLab?.settings?.pasf_reflex?.active;

  if (pashReflexEnabled)
    protocolsToShow.push(SpecimenProtocolOverride.protocol.PASH_REFLEX);

  if (pasfReflexEnabled)
    protocolsToShow.push(SpecimenProtocolOverride.protocol.PASF_REFLEX);

  const slowMohsEnabled = useAppSelector(
    (state) => !!state.sys.features.switches?.slowMohsProtocol
  );
  if (slowMohsEnabled)
    protocolsToShow.push(SpecimenProtocolOverride.protocol.SLOW_MOHS);

  return (
    <fieldset className="panelContainer__section">
      <legend className="panelContainer__section--title">Protocols</legend>
      {protocolsToShow.map((p) => (
        <div style={{ display: "flex" }} key={p}>
          <div
            className={classNames("Tag", {
              "Tag--blue":
                (protocolOverrides || []).find((po) => po.protocol === p) ===
                  undefined &&
                protocolStatuses[p as SpecimenProtocolOverride.protocol].active,
              "Tag--gray":
                (protocolOverrides || []).find((po) => po.protocol === p) ===
                  undefined &&
                !protocolStatuses[p as SpecimenProtocolOverride.protocol]
                  .active,
              "Tag--blue--bordered":
                (protocolOverrides || []).find((po) => po.protocol === p)
                  ?.active === true,
              "Tag--gray--bordered":
                (protocolOverrides || []).find((po) => po.protocol === p)
                  ?.active === false,
            })}
            data-tip={
              protocolStatuses[p as SpecimenProtocolOverride.protocol].reason
            }
            data-for="specimen-form-protocols"
          >
            {protocolLabels[p as SpecimenProtocolOverride.protocol]}
          </div>
          <div style={{ marginLeft: "auto" }}>
            <select
              onChange={(ev) =>
                setProtocolOverride(
                  p as SpecimenProtocolOverride.protocol,
                  ev.currentTarget.value
                )
              }
              value={
                protocolOverrides.find((po) => po.protocol === p) === undefined
                  ? "null"
                  : protocolOverrides.find((po) => po.protocol === p)?.active
                  ? "true"
                  : "false"
              }
            >
              <option value="null">
                Auto:{" "}
                {protocolStatuses[p as SpecimenProtocolOverride.protocol].active
                  ? "ON"
                  : "OFF"}
              </option>
              <option value="true">Override: ON</option>
              <option value="false">Override: OFF</option>
            </select>
          </div>
        </div>
      ))}
      {protocolsStr !== initialProtocolsStr && (
        <div style={{ fontStyle: "italic", color: "darkred" }}>
          Changing Protocols after Accessioning is not fully supported.
        </div>
      )}
      <ReactTooltip id="specimen-form-protocols" place="right" />
    </fieldset>
  );
};

export default SpecimenFormProtocols;
